import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {
	getUserOrder,
	updateOrderStatus,
} from '../../redux/actions/orderActions';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Heading,
	Text,
	Image,
	Spinner,
	Center,
	Flex,
	Container,
	Divider,
	SimpleGrid,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Select,
	Button,
	HStack,
	Card,
	CardBody,
	Icon,
	Stack,
	Stat,
	StatLabel,
	StatNumber,
	StatHelpText,
} from '@chakra-ui/react';
import {
	MdShoppingBag,
	MdCalendarToday,
	MdEmail,
	MdLocalShipping,
	MdAttachMoney,
	MdTag,
	MdEdit,
} from 'react-icons/md';
import { ArrowBackIcon } from '@chakra-ui/icons';
import Alert from '../../component/alert';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const STATUS_COLORS = {
	Completed: 'green',
	Processing: 'yellow',
	Shipped: 'cyan',
	Pending: 'blue',
	Canceled: 'red',
};

const InfoCard = ({ icon, label, value, highlight = false }) => (
	<Card borderRadius="xl" shadow="sm" h="100%">
		<CardBody>
			<Flex align="center" gap={3} mb={1}>
				<Flex
					w="32px" h="32px"
					borderRadius="md"
					bg={highlight ? 'teal.50' : 'blue.50'}
					align="center"
					justify="center"
					flexShrink={0}
				>
					<Icon as={icon} boxSize={4} color={highlight ? 'teal.500' : 'blue.500'} />
				</Flex>
				<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
					{label}
				</Text>
			</Flex>
			<Text fontWeight="semibold" color="gray.800" fontSize="sm" mt={1} noOfLines={2}>
				{value || '—'}
			</Text>
		</CardBody>
	</Card>
);

const AdminOrderDetail = ({
	getUserOrder,
	loading,
	orders,
	updateOrderStatus,
}) => {
	const { order_id } = useParams();
	const navigate = useNavigate();
	const [newStatus, setNewStatus] = useState('Processing');
	const [isUpdating, setIsUpdating] = useState(false);
	const [fishbowlLink, setFishbowlLink] = useState<any>(null);
	const [fishbowlBusy, setFishbowlBusy] = useState(false);
	const [fishbowlMsg, setFishbowlMsg] = useState('');

	useEffect(() => {
		if (order_id) {
			getUserOrder(order_id);
		}
	}, [order_id]);

	useEffect(() => {
		if (!order_id) return;
		const token = localStorage.getItem('x-auth-token');
		fetch(`${BASE_URL}/api/fishbowl/orders/${order_id}`, {
			headers: { 'x-auth-token': token || '' },
		})
			.then((r) => r.json())
			.then((data) => setFishbowlLink(data.link || null))
			.catch(() => setFishbowlLink(null));
	}, [order_id, orders]);

	const fishbowlAction = async (action: 'send' | 'refresh') => {
		setFishbowlBusy(true);
		setFishbowlMsg('');
		try {
			const token = localStorage.getItem('x-auth-token');
			const res = await fetch(`${BASE_URL}/api/fishbowl/orders/${order_id}/${action}`, {
				method: 'POST',
				headers: { 'x-auth-token': token || '', 'Content-Type': 'application/json' },
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || 'Fishbowl request failed');
			setFishbowlLink(data.link || null);
			setFishbowlMsg(data.message || 'Done');
			if (action === 'send') getUserOrder(order_id);
		} catch (e: any) {
			setFishbowlMsg(e.message || 'Fishbowl error');
		} finally {
			setFishbowlBusy(false);
		}
	};

	const downloadFishbowlCsv = async () => {
		setFishbowlBusy(true);
		setFishbowlMsg('');
		try {
			const token = localStorage.getItem('x-auth-token');
			const res = await fetch(`${BASE_URL}/api/fishbowl/orders/${order_id}/csv`, {
				headers: { 'x-auth-token': token || '' },
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				throw new Error(data.error || 'Failed to download CSV');
			}
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `fishbowl-so-order-${order_id}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			setFishbowlMsg('CSV downloaded.');
		} catch (e: any) {
			setFishbowlMsg(e.message || 'CSV download failed');
		} finally {
			setFishbowlBusy(false);
		}
	};

	const orderDetails = orders && orders.length > 0 ? orders[0] : null;
	const total =
		orders?.reduce((acc, item) => {
			return acc + parseFloat(item.price || 0) * parseFloat(item.quantity || 1);
		}, 0) || 0;

	useEffect(() => {
		if (orderDetails?.status) {
			setNewStatus(orderDetails.status);
		}
	}, [orderDetails]);

	const handleStatusUpdate = async () => {
		if (!newStatus || !order_id) return;
		setIsUpdating(true);
		await updateOrderStatus(order_id, newStatus);
		setIsUpdating(false);
	};

	return (
		<>
			<AdminNavbar />
			<Box ml={{ base: 0, md: '250px' }} bg="gray.50" minH="100vh">
				<Alert />
				<Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
					{loading ? (
						<Center minH="60vh">
							<Spinner size="xl" thickness="4px" color="blue.500" />
						</Center>
					) : orderDetails ? (
						<>
							{/* Page Header */}
							<Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={3}>
								<HStack spacing={3}>
									<Flex
										w="44px" h="44px"
										borderRadius="xl"
										bg="blue.50"
										align="center"
										justify="center"
									>
										<Icon as={MdShoppingBag} boxSize={6} color="blue.500" />
									</Flex>
									<Box>
										<Heading size="lg" color="gray.800">Order #{orderDetails.order_id}</Heading>
										<Text fontSize="sm" color="gray.400">
											Placed {new Date(orderDetails.created_at).toLocaleDateString('en-US', {
												year: 'numeric', month: 'long', day: 'numeric',
											})}
										</Text>
									</Box>
								</HStack>
								<Button
									leftIcon={<ArrowBackIcon />}
									variant="ghost"
									size="sm"
									onClick={() => navigate(-1)}
									color="gray.500"
								>
									Back to Orders
								</Button>
							</Flex>

							{/* KPI Row */}
							<SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={6}>
								<InfoCard
									icon={MdTag}
									label="Order #"
									value={`#${orderDetails.order_id}`}
								/>
								<InfoCard
									icon={MdCalendarToday}
									label="Date"
									value={new Date(orderDetails.created_at).toLocaleDateString('en-US', {
										month: 'short', day: 'numeric', year: 'numeric',
									})}
								/>
								<InfoCard
									icon={MdAttachMoney}
									label="Order Total"
									value={`$${total.toFixed(2)}`}
									highlight
								/>
								<InfoCard
									icon={MdEmail}
									label="Customer"
									value={orderDetails.email}
								/>
								<InfoCard
									icon={MdLocalShipping}
									label="Ship To"
									value={orderDetails.shipping_address}
								/>
								<Card borderRadius="xl" shadow="sm">
									<CardBody>
										<Flex align="center" gap={3} mb={1}>
											<Flex
												w="32px" h="32px"
												borderRadius="md"
												bg="purple.50"
												align="center"
												justify="center"
												flexShrink={0}
											>
												<Icon as={MdEdit} boxSize={4} color="purple.500" />
											</Flex>
											<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
												Status
											</Text>
										</Flex>
										<Badge
											colorScheme={STATUS_COLORS[orderDetails.status] || 'gray'}
											variant="subtle"
											borderRadius="full"
											px={3}
											py={1}
											fontSize="sm"
											mt={1}
										>
											{orderDetails.status || 'Pending'}
										</Badge>
									</CardBody>
								</Card>
							</SimpleGrid>

							{/* PO Number + Notes Card */}
							{(orderDetails.po_number || orderDetails.notes) && (
								<Card borderRadius="2xl" shadow="sm" mb={6}>
									<CardBody>
										<SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
											{orderDetails.po_number && (
												<Box>
													<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>PO Number</Text>
													<Text fontWeight="semibold" color="gray.800">{orderDetails.po_number}</Text>
												</Box>
											)}
											{orderDetails.notes && (
												<Box>
													<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>Order Notes</Text>
													<Text color="gray.700" fontSize="sm">{orderDetails.notes}</Text>
												</Box>
											)}
										</SimpleGrid>
									</CardBody>
								</Card>
							)}

						{/* Status Update Card */}
							<Card borderRadius="2xl" shadow="sm" mb={6}>
								<CardBody>
									<Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
										<Box>
											<Text fontWeight="semibold" color="gray.700" mb={0.5}>Update Order Status</Text>
											<Text fontSize="sm" color="gray.400">
												Current status: <strong>{orderDetails.status}</strong>
											</Text>
										</Box>
										<HStack spacing={3}>
											<Select
												value={newStatus}
												onChange={(e) => setNewStatus(e.target.value)}
												maxW="180px"
												size="sm"
												borderRadius="lg"
											>
												<option value="Pending">Pending</option>
												<option value="Processing">Processing</option>
												<option value="Shipped">Shipped</option>
												<option value="Completed">Completed</option>
												<option value="Canceled">Canceled</option>
											</Select>
											<Button
												colorScheme="teal"
												size="sm"
												borderRadius="lg"
												onClick={handleStatusUpdate}
												isLoading={isUpdating}
												isDisabled={newStatus === orderDetails.status}
												flexShrink={0}
											>
												Save
											</Button>
										</HStack>
									</Flex>
								</CardBody>
							</Card>

							{/* Fishbowl */}
							<Card borderRadius="2xl" shadow="sm" mb={6} borderColor="orange.100" borderWidth="1px">
								<CardBody>
									<Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
										<Box>
											<Text fontWeight="semibold" color="gray.700" mb={0.5}>Fishbowl</Text>
											{fishbowlLink?.fishbowl_so_num ? (
												<Stack spacing={0.5} fontSize="sm" color="gray.600">
													<Text>SO #: <strong>{fishbowlLink.fishbowl_so_num}</strong></Text>
													{fishbowlLink.customer_po && <Text>Customer PO: {fishbowlLink.customer_po}</Text>}
													{fishbowlLink.status_id != null && <Text>Status ID: {fishbowlLink.status_id}</Text>}
													{fishbowlLink.total_tax != null && <Text>Tax: ${Number(fishbowlLink.total_tax).toFixed(2)}</Text>}
													{fishbowlLink.last_error && <Text color="red.500">{fishbowlLink.last_error}</Text>}
												</Stack>
											) : (
												<Text fontSize="sm" color="gray.400">Not sent to Fishbowl yet.</Text>
											)}
											{fishbowlMsg && (
												<Text fontSize="sm" mt={2} color={fishbowlMsg.toLowerCase().includes('fail') || fishbowlMsg.toLowerCase().includes('error') || fishbowlMsg.toLowerCase().includes('missing') ? 'red.500' : 'green.600'}>
													{fishbowlMsg}
												</Text>
											)}
										</Box>
										<HStack spacing={3} flexWrap="wrap">
											<Button
												colorScheme="orange"
												size="sm"
												borderRadius="lg"
												onClick={() => fishbowlAction('send')}
												isLoading={fishbowlBusy}
												isDisabled={!!(fishbowlLink?.pushed_at && !fishbowlLink?.last_error)}
												title={
													fishbowlLink?.pushed_at && !fishbowlLink?.last_error
														? 'Already sent to Fishbowl'
														: undefined
												}
											>
												{fishbowlLink?.pushed_at && !fishbowlLink?.last_error
													? 'Sent to Fishbowl'
													: 'Send to Fishbowl'}
											</Button>
											<Button
												variant="outline"
												colorScheme="orange"
												size="sm"
												borderRadius="lg"
												onClick={() => fishbowlAction('refresh')}
												isLoading={fishbowlBusy}
												isDisabled={!fishbowlLink?.fishbowl_so_num && !order_id}
											>
												Refresh from Fishbowl
											</Button>
											<Button
												variant="outline"
												size="sm"
												borderRadius="lg"
												onClick={downloadFishbowlCsv}
												isLoading={fishbowlBusy}
											>
												Download CSV
											</Button>
										</HStack>
									</Flex>
								</CardBody>
							</Card>

							{/* Items Table Card */}
							<Card borderRadius="2xl" shadow="sm" overflow="hidden">
								<CardBody pb={0}>
									<Flex align="center" gap={2} mb={4}>
										<Heading size="sm" color="gray.700">Items Ordered</Heading>
										<Badge colorScheme="blue" variant="subtle" borderRadius="full">
											{orders.length} item{orders.length !== 1 ? 's' : ''}
										</Badge>
									</Flex>
								</CardBody>
								<Box overflowX="auto">
									<Table variant="simple" size="sm">
										<Thead>
											<Tr bg="gray.50">
												<Th fontSize="xs" color="gray.500">Product</Th>
												<Th fontSize="xs" color="gray.500">FB #</Th>
												<Th fontSize="xs" color="gray.500">ID</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Qty</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Unit Price</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Subtotal</Th>
											</Tr>
										</Thead>
										<Tbody>
											{orders.map((item) => (
												<Tr key={item.product_id} _hover={{ bg: 'gray.50' }}>
													<Td>
														<Flex align="center" gap={3}>
															<Image
																src={`${BASE_URL}/api/image?filename=${item.image}`}
																boxSize="44px"
																objectFit="contain"
																borderRadius="lg"
																bg="white"
																border="1px solid"
																borderColor="gray.100"
															/>
															<Box>
																<Text fontWeight="semibold" fontSize="sm" color="gray.800">
																	{item.product_name}
																</Text>
																{item.variant_label && (
																	<Badge colorScheme="purple" fontSize="xs" mt={0.5}>{item.variant_label}</Badge>
																)}
																<Text fontSize="xs" color="gray.400" noOfLines={1}>
																	{item.product_description}
																</Text>
															</Box>
														</Flex>
													</Td>
													<Td>
														<Text fontSize="xs" color="gray.700" fontFamily="mono">
															{item.fb_number || '—'}
														</Text>
													</Td>
													<Td><Text fontSize="xs" color="gray.500">#{item.product_id}</Text></Td>
													<Td isNumeric>
														<Text fontWeight="medium">{item.quantity}</Text>
													</Td>
													<Td isNumeric>
														<Text color="gray.600">${parseFloat(item.price).toFixed(2)}</Text>
													</Td>
													<Td isNumeric>
														<Text fontWeight="semibold" color="gray.800">
															${(parseFloat(item.price) * parseFloat(item.quantity || 1)).toFixed(2)}
														</Text>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
								<Box px={6} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
									<Flex justify="flex-end">
										<HStack spacing={6}>
											<Text color="gray.500" fontSize="sm">
												{orders.length} item{orders.length !== 1 ? 's' : ''}
											</Text>
											<HStack>
												<Text color="gray.500" fontSize="sm">Order Total</Text>
												<Text fontWeight="bold" fontSize="lg" color="teal.600">
													${total.toFixed(2)}
												</Text>
											</HStack>
										</HStack>
									</Flex>
								</Box>
							</Card>
						</>
					) : (
						<Center minH="60vh" flexDirection="column" gap={4}>
							<Flex
								w="64px" h="64px"
								borderRadius="2xl"
								bg="gray.100"
								align="center"
								justify="center"
							>
								<Icon as={MdShoppingBag} boxSize={8} color="gray.400" />
							</Flex>
							<Heading size="md" color="gray.600">Order Not Found</Heading>
							<Button
								leftIcon={<ArrowBackIcon />}
								colorScheme="blue"
								borderRadius="xl"
								onClick={() => navigate(-1)}
							>
								Back to Orders
							</Button>
						</Center>
					)}
				</Container>
			</Box>
		</>
	);
};

const mapStateToProps = (state) => ({
	orders: state.order.userorder,
	loading: state.order.userloading,
});

export default connect(mapStateToProps, { getUserOrder, updateOrderStatus })(
	AdminOrderDetail
);
